import { useState, useRef, useEffect, useContext } from 'react';
import { Button } from '../../../ui/button';
import {
  Person,
  Pen,
  Folder,
  Share,
  ExclamationCircle,
  ArrowCounterclockwise,
  ArrowClockwise,
  List,
  BarChartLine,
  Archive,
  Table,
  Trash,
  FileText,
  Eye,
  ArrowRepeat
} from 'react-bootstrap-icons';
import { cn } from '../../../../lib/utils';
import { LocalizeContext } from 'react-locale-language';
import { isRtl } from '../../../../helpers/localization';
import ExplanatoryVideoLayout from '../../../common/ExplanatoryVideoLayout';

export default function CustomerNavbar({
  showHowToUseApp,
  setShowHowToUseApp,
  onEditClick,
  onCustomerGeneralInfoClick,
  onCompareStrategyClick,
  onViewFilesClick,
  onShareClick,
  onUndoClick,
  onRedoClick,
  onLogsClick,
  onArchiveClick,
  onDeleteClick,
  onCompTableClick,
  activeCustomerId,
  fromCustomerPortal,
  readOnlyMode,
  onUpdateGroupStrategiesClick,
  parentCustomer,
  activeTab
}) {
  const { translate, langCode } = useContext(LocalizeContext);

  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSettingsAction = action => {
    setShowSettingsDropdown(false);
    switch (action) {
      case 'archive':
        onArchiveClick();
        break;
      case 'comp-table':
        onCompTableClick();
        break;
      case 'update-group-strategies':
        onUpdateGroupStrategiesClick();
        break;
      case 'delete':
        onDeleteClick();
        break;
      case 'logs':
        onLogsClick();
        break;
      case 'view-as-client':
        window.open(`${process.env.PUBLIC_URL}/customer?customerId=${activeCustomerId}`);
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-between px-4 gap-4">
      <div className="flex text-black w-full items-center justify-between">
        <div className="flex gap-6 overflow-auto mr-2">
          <NavButton Icon={Pen} label="edit" onClick={onEditClick} hide={fromCustomerPortal || readOnlyMode} />

          <NavButton
            Icon={Person}
            label="client_info"
            onClick={onCustomerGeneralInfoClick}
            hide={readOnlyMode || parentCustomer.level <= 1}
          />

          <NavButton
            Icon={BarChartLine}
            label="compare_strategies"
            onClick={onCompareStrategyClick}
            hide={activeTab !== 'rujum'}
          />

          <NavButton
            Icon={Folder}
            label="recordings"
            onClick={onViewFilesClick}
            hide={readOnlyMode || parentCustomer.level <= 1}
          />

          <NavButton Icon={Share} label="share" onClick={onShareClick} hide={readOnlyMode} />

          <NavButton
            onClick={() => setShowHowToUseApp(!showHowToUseApp)}
            isActive={showHowToUseApp}
            Icon={ExclamationCircle}
            label="how_to_use_app"
            hide={activeTab !== 'rujum'}
          />

          <NavButton
            Icon={ArrowCounterclockwise}
            label="undo"
            onClick={onUndoClick}
            hide={fromCustomerPortal || readOnlyMode}
          />

          <NavButton
            Icon={ArrowClockwise}
            label="redo"
            onClick={onRedoClick}
            hide={fromCustomerPortal || readOnlyMode}
          />
        </div>

        {/* Custom Settings Dropdown */}
        {!fromCustomerPortal && !readOnlyMode && (
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center gap-1 px-3 py-1 border-none rounded-full bg-[#ECE2F5] text-[#4F16D3] hover:bg-[#D8C8F0] transition-colors cursor-pointer text-xs"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            >
              <List className="customer-nav-icon" />
              <span>{translate('settings')}</span>
            </div>

            {showSettingsDropdown && (
              <div
                className={`absolute top-full mt-1 w-max bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-clip ${
                  isRtl(langCode) ? 'left-0' : 'right-0'
                }`}
              >
                <div className="py-1">
                  <button
                    onClick={() => handleSettingsAction('archive')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors m-0"
                  >
                    <Archive className="h-4 w-4 text-gray-600" />
                    <span>{parentCustomer.archived ? translate('unarchive_client') : translate('archive_client')}</span>
                  </button>

                  <button
                    onClick={() => handleSettingsAction('comp-table')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors m-0"
                  >
                    <Table className="h-4 w-4 text-gray-600" />
                    <span>{translate('client_comp_table')}</span>
                  </button>

                  <button
                    onClick={() => handleSettingsAction('update-group-strategies')}
                    className="flex items-center gap-2 w-max px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors m-0"
                  >
                    <ArrowRepeat className="h-4 w-4 text-gray-600" />
                    <span>{translate('update_group_strategies')}</span>
                  </button>

                  <div className="border-t border-gray-200 my-1"></div>

                  <button
                    onClick={() => handleSettingsAction('logs')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors m-0"
                  >
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span>{translate('logs')}</span>
                  </button>

                  <button
                    onClick={() => handleSettingsAction('view-as-client')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors m-0"
                  >
                    <Eye className="h-4 w-4 text-gray-600" />
                    <span>{translate('view_as_client')}</span>
                  </button>

                  <div className="border-t border-gray-200 my-1"></div>

                  <button
                    onClick={() => handleSettingsAction('delete')}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors m-0"
                  >
                    <Trash className="h-4 w-4" />
                    <span>{translate('delete_client')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {showHowToUseApp && (
        <ExplanatoryVideoLayout
          videoKey="how_to_use_app"
          showPlayer={showHowToUseApp}
          setShowPlayer={setShowHowToUseApp}
        />
      )}
    </div>
  );
}

const NavButton = ({ Icon, label, onClick, variant = 'link', className, isActive = false, hide = false }) => {
  const { translate } = useContext(LocalizeContext);

  if (hide) return null;

  return (
    <Button
      variant={variant}
      size="sm"
      className={cn(
        'bg-transparent text-xs flex items-center gap-1 hover:no-underline p-0 m-0 text-black',
        className,
        isActive && 'text-primary bg-primary-light px-3 py-1'
      )}
      onClick={onClick}
    >
      <Icon className="customer-nav-icon" />
      <span>{translate(label)}</span>
    </Button>
  );
};
